import controllerModule from "@ctrl/controller";
import type {
  Block,
  BlockId,
  Controller,
  Document,
  RoutineOpenaiMessageRole,
  TaskId,
} from "@ctrl/controller-rpc";

import { proseMirrorDocToDocument } from "@/components/editor/document-to-prosemirror";
import { parseMarkupToDoc } from "@/components/editor/serialize";
import type { BaseLogger } from "@/components/inject";

const { makeBlockId } = controllerModule;

export type DisplayCitation = { url: string; title?: string };

export type DisplayMessage = {
  content: string;
  citations?: DisplayCitation[];
  document?: Document;
} & RoutineOpenaiMessageRole;

export function isDisplayCitation(value: unknown): value is DisplayCitation {
  return (
    value !== null &&
    typeof value === "object" &&
    "url" in value &&
    typeof value.url === "string" &&
    (!("title" in value) ||
      value.title === undefined ||
      typeof value.title === "string")
  );
}

/**
 * Lookup for the sanitizer below. For a given task id:
 * - a string is the title of an existing task,
 * - `null` means the task does not exist,
 * - `undefined` means the task has not been resolved (yet).
 */
export type TaskTitleLookup = (taskId: TaskId) => string | null | undefined;

/** Collect the task ids referenced by `todo` blocks in an AI document. */
export function collectTodoTaskIds(document: Document): TaskId[] {
  return document.blocks.flatMap((block) =>
    block.type === "todo" && block.task ? [block.task] : []
  );
}

/**
 * Resolve which of the given task ids point to an existing task, and their
 * titles. AI documents may reference hallucinated or deleted tasks, so ids
 * that don't resolve map to `null`.
 */
export async function resolveTaskTitles(
  controller: Controller,
  taskIds: TaskId[]
): Promise<Map<TaskId, string | null>> {
  const uniqueIds = [...new Set(taskIds)];
  const entries = await Promise.all(
    uniqueIds.map(async (id): Promise<[TaskId, string | null]> => {
      try {
        const taskRef = await controller.task.get(id);
        const title = taskRef.value?.title ?? null;
        taskRef.release();
        return [id, title];
      } catch {
        return [id, null];
      }
    })
  );
  return new Map(entries);
}

/**
 * AI-generated documents may contain `todo` blocks that are not backed by an
 * actual task (no task id, hallucinated/deleted task id, or no title).
 * Rendering those as real task widgets is broken (clicking them tries to
 * spawn/wait for a task), so demote them to plain `check` blocks.
 *
 * A `todo` block is kept only when its task id resolves to an existing task
 * (per `taskTitle`) and it ends up with a non-empty title — using the real
 * task's title when the AI left the block content empty.
 */
export function sanitizeAiDocument(
  document: Document,
  taskTitle: TaskTitleLookup
): Document {
  return {
    blocks: document.blocks.map((block) => {
      if (block.type !== "todo") return block;
      const title = block.task ? taskTitle(block.task) : undefined;
      if (typeof title === "string") {
        const content = block.content.trim() ? block.content : title;
        if (content.trim()) {
          return {
            id: block.id,
            type: "todo",
            checked: block.checked,
            content,
            task: block.task,
          };
        }
      }
      return {
        id: block.id,
        type: "check",
        checked: block.checked,
        content: block.content,
      };
    }),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function blockId(id: unknown): BlockId {
  return typeof id === "string" && id.length > 0 ? id : makeBlockId();
}

/**
 * Convert one block from either the model's rich-response shape — where the
 * payload is nested under `content` (see `supported_block` in the controller's
 * `tools/routine/document.ml`) — or an already-flat controller block.
 *
 * Returns `"skip"` for `existing` blocks (they reference the previous version
 * of an edited document, which conversation messages don't carry), and `null`
 * when the shape is not recognized.
 */
function supportedBlockToBlock(raw: unknown): Block | "skip" | null {
  if (!isRecord(raw) || typeof raw.type !== "string") return null;
  const id = blockId(raw.id);
  const payload = isRecord(raw.content) ? raw.content : raw;
  switch (raw.type) {
    case "paragraph":
    case "blockquote":
    case "embed": {
      if (!payload || typeof payload.content !== "string") return null;
      return { id, type: raw.type, content: payload.content };
    }
    case "bullet": {
      if (
        !payload ||
        typeof payload.content !== "string" ||
        (payload.list_type !== "ordered" && payload.list_type !== "unordered")
      )
        return null;
      return {
        id,
        type: "bullet",
        list_type: payload.list_type,
        content: payload.content,
        ...(typeof payload.depth === "number" ? { depth: payload.depth } : {}),
      };
    }
    case "check": {
      if (
        !payload ||
        typeof payload.content !== "string" ||
        typeof payload.checked !== "boolean"
      )
        return null;
      return {
        id,
        type: "check",
        checked: payload.checked,
        content: payload.content,
        ...(typeof payload.depth === "number" ? { depth: payload.depth } : {}),
      };
    }
    case "code": {
      if (
        !payload ||
        typeof payload.content !== "string" ||
        typeof payload.language !== "string"
      )
        return null;
      return {
        id,
        type: "code",
        language: payload.language,
        content: payload.content,
      };
    }
    case "divider":
      return { id, type: "divider" };
    case "heading": {
      if (
        !payload ||
        typeof payload.content !== "string" ||
        typeof payload.level !== "number"
      )
        return null;
      return {
        id,
        type: "heading",
        content: payload.content,
        level: payload.level,
        ...(typeof payload.retracted === "boolean"
          ? { retracted: payload.retracted }
          : {}),
      };
    }
    case "todo": {
      if (
        !payload ||
        typeof payload.content !== "string" ||
        typeof payload.checked !== "boolean"
      )
        return null;
      return {
        id,
        type: "todo",
        checked: payload.checked,
        content: payload.content,
        ...(typeof payload.task === "string" && payload.task
          ? { task: payload.task }
          : {}),
      };
    }
    case "existing":
      return "skip";
    default:
      return null;
  }
}

/**
 * Convert a rich-response document to a flat controller `Document`. Persisted
 * assistant messages can contain either an array of model-shaped blocks or a
 * single already-flat block. Returns `null` when any block has an unrecognized
 * shape, so the caller can fall back to rendering the message as plain text
 * instead of crashing downstream — the rich text parser throws on non-string
 * block contents.
 */
function supportedDocumentToDocument(value: unknown): Document | null {
  if (!isRecord(value)) return null;
  const rawBlocks = Array.isArray(value.blocks)
    ? value.blocks
    : isRecord(value.blocks)
      ? [value.blocks]
      : null;
  if (!rawBlocks) return null;
  const blocks: Block[] = [];
  for (const raw of rawBlocks) {
    const block = supportedBlockToBlock(raw);
    if (block === null) return null;
    if (block !== "skip") blocks.push(block);
  }
  return { blocks };
}

export function parseRichContent(
  content: string
): { document: Document; citations?: DisplayCitation[] } | null {
  try {
    const parsed: unknown = JSON.parse(content);
    if (parsed !== null && typeof parsed === "object" && "document" in parsed) {
      const document = supportedDocumentToDocument(parsed.document);
      if (document) {
        const citations =
          "citations" in parsed &&
          Array.isArray(parsed.citations) &&
          parsed.citations.every(isDisplayCitation)
            ? parsed.citations
            : undefined;
        return { document, citations };
      }
    }
  } catch {
    // plain text content — not a rich response
  }
  return null;
}

export function buildConversationDocument(
  messages: DisplayMessage[],
  makeBlockId: () => string,
  agentName: string,
  logger: BaseLogger
): Document {
  const headingBlock = (content: string, level: number): Block => ({
    id: makeBlockId(),
    type: "heading",
    content,
    level,
  });
  const dividerBlock = (): Block => ({
    id: makeBlockId(),
    type: "divider",
  });
  const orderedBulletBlock = (content: string): Block => ({
    id: makeBlockId(),
    type: "bullet",
    list_type: "ordered",
    content,
  });
  const citationMarkdownLink = (citation: DisplayCitation): string => {
    const label = (citation.title || citation.url).replace(/\]/g, "\\]");
    const url = citation.url.replace(/[()]/g, "\\$&");
    return `[${label}](${url})`;
  };
  const messageBlocks = (msg: DisplayMessage): Block[] => {
    if (msg.document) return msg.document.blocks;
    return proseMirrorDocToDocument(parseMarkupToDoc(msg.content, logger))
      .blocks;
  };
  const messageLabel = (msg: DisplayMessage): string => {
    if (msg.role === "user") return "You";
    if (msg.role === "assistant") return agentName;
    return msg.role;
  };

  const blocks: Block[] = [];
  messages.forEach((msg, index) => {
    if (index > 0) blocks.push(dividerBlock());
    blocks.push(headingBlock(messageLabel(msg), 2));
    blocks.push(...messageBlocks(msg));
    if (msg.citations?.length) {
      blocks.push(headingBlock("Sources", 3));
      blocks.push(
        ...msg.citations.map((citation) =>
          orderedBulletBlock(citationMarkdownLink(citation))
        )
      );
    }
  });
  return { blocks };
}
